/**
 * The key list (docs/ux.md §12) and the replay bar for watching the opponent's turn (§11.3).
 */
import type { GameState } from '@brig/rules-engine';
import { useTranslation } from 'react-i18next';
import type { Messages } from '../i18n/locales/en.ts';
import * as styles from './Game.css.ts';
import { lastTurnRange } from './turnMachine.ts';
import type { TurnSend, TurnSnapshot } from './useTurn.ts';

const KEYS: readonly (readonly [string, keyof Messages['game']['keys']])[] = [
  ['Space', 'space'],
  ['Tab / Shift+Tab', 'tab'],
  ['← ↑ → ↓', 'arrows'],
  ['Enter', 'enter'],
  ['F', 'f'],
  ['V', 'v'],
  ['S', 's'],
  ['A', 'a'],
  ['Z', 'z'],
  ['R', 'r'],
  ['I', 'i'],
  ['Esc', 'esc'],
  ['M', 'm'],
  ['C', 'c'],
  ['L', 'l'],
  ['+ / −', 'plusMinus'],
  ['?', 'help'],
];

/** §12: every shortcut with what it does. */
export function KeysList() {
  const { t } = useTranslation();
  return (
    <dl className={styles.keys}>
      {KEYS.map(([key, what]) => (
        <div key={what} className={styles.keyRow}>
          <dt>
            <kbd>{key}</kbd>
          </dt>
          <dd>{t(`game.keys.${what}`)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** "Watch …'s turn", where a replay can start: the curtain and the game over card. */
export function WatchButton({ snapshot, send }: { snapshot: TurnSnapshot; send: TurnSend }) {
  const { t } = useTranslation();
  const history = snapshot.context.history;
  const range = lastTurnRange(history);
  if (!range) return null;
  const side = history.states[range.from]?.turn.side;
  const name = (side && history.states[range.from]?.players[side].name) ?? '';
  return (
    <button
      type="button"
      className={styles.button}
      onClick={() => {
        send({ type: 'WATCH' });
      }}
    >
      {t('game.replay.watch', { side: name })}
    </button>
  );
}

/** U-LOG-05: what the replay shows and its controls. */
export function ReplayBar({ snapshot, send }: { snapshot: TurnSnapshot; send: TurnSend }) {
  const { t } = useTranslation();
  const replay = snapshot.context.replay;
  if (!replay) return null;
  const shown: GameState | undefined = snapshot.context.history.states[replay.at];
  const side = shown ? shown.players[shown.turn.side].name : '';
  const paused = snapshot.matches({ replay: 'paused' });
  return (
    <div className={styles.replayBar} role="region" aria-label={t('game.replay.title', { side })}>
      <strong>{t('game.replay.title', { side })}</strong>
      <span className={styles.hint} aria-live="polite">
        {t('game.replay.step', { n: replay.at - replay.from, total: replay.to - replay.from })}
      </span>
      <span className={styles.spacer} />
      <button
        type="button"
        className={styles.button}
        onClick={() => {
          send({ type: paused ? 'REPLAY_PLAY' : 'REPLAY_PAUSE' });
        }}
      >
        {paused ? t('game.replay.play') : t('game.replay.pause')}
      </button>
      <button
        type="button"
        className={styles.button}
        aria-pressed={replay.speed === 2}
        title={t('game.replay.speed')}
        onClick={() => {
          send({ type: 'REPLAY_SPEED', speed: replay.speed === 2 ? 1 : 2 });
        }}
      >
        {replay.speed === 2 ? '2×' : '1×'}
      </button>
      <button
        type="button"
        className={styles.primary}
        onClick={() => {
          send({ type: 'REPLAY_SKIP' });
        }}
      >
        {t('game.replay.skip')}
      </button>
    </div>
  );
}
